import React, { useState, useContext, useEffect, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Button, Menu } from 'semantic-ui-react';
import { Text } from 'components/typography';
import { SearchInput } from 'components/form-elements/inputs/SearchInput';
import { useAllUsers } from 'services/admin/get-all-users';
import { useUserPermissions } from 'services/user/permissions';
import { Brikk } from 'components/Brikk';
import { PageLoader } from 'components/loaders/PageLoader';
import { useModal } from 'store/modals';
import { ManualScrollToTop } from 'components/ManualScrollToTop';
import { UserAdministrationModalContext } from '../../../../UserAdministrationModalContext';
import { ACTIVE_TAB, PENDING_TAB } from '../../../../constants';
import styles from './styles.module.scss';
import { UserItem } from './components/UserItem';

const PENDING_USER_STATUS = 'FORCE_CHANGE_PASSWORD';

export const UsersList = () => {

  const userListRef = useRef(null);
  const modal = useModal();
  const userAdministrationContext = useContext(UserAdministrationModalContext);
  const { users, isLoading, isSuccess } = useAllUsers();
  const { checkPermission } = useUserPermissions();

  const [keyword, setKeyword] = useState('');

  const intl = useIntl();

  const isAdmin = checkPermission('admin-users', 'read');
  const isSystemAdmin = checkPermission('system-admin', 'create', 'read', 'update', 'delete', 'assign');
  const canSeeTabs = isAdmin || isSystemAdmin;
  const canAddNewUser = isSystemAdmin || checkPermission('admin-users', 'create');
  const sortedUsers = [...users].sort((a, b) => -b.firstName.localeCompare(a.firstName));

  const usersForTab = !canSeeTabs
    ? sortedUsers
    : sortedUsers.filter((user) => (
      userAdministrationContext.activeTab === PENDING_TAB
        ? user.userStatus === PENDING_USER_STATUS
        : user.userStatus !== PENDING_USER_STATUS
    ));

  const filteredUsers = !keyword
    ? usersForTab
    : usersForTab.filter((user) => (
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(keyword.toLowerCase())
      || (user.addressId && String(user.addressId).toLowerCase().includes(keyword.toLowerCase()))
    ));

  useEffect(() => {
    if (userAdministrationContext.invitedUser && isSuccess) {
      const addedUser = users.find((user) => user.email === userAdministrationContext.invitedUser);
      if (addedUser) {
        userAdministrationContext.setSelectedUser(addedUser);
      }
    }
  }, [users, userAdministrationContext.invitedUser, userAdministrationContext.setSelectedUser, isSuccess]);

  if (isLoading && !userAdministrationContext.invitedUser) return <PageLoader />;

  return (
    <div
      className={styles.userListContainer}
    >
      <Brikk.Heading heading="currentUsers" />

      <div
        className={styles.users}
        ref={userListRef}
      >
        {canSeeTabs && (
        <Menu
          pointing
          secondary
        >
          <Menu.Item
            name={ACTIVE_TAB}
            active={userAdministrationContext.activeTab === ACTIVE_TAB}
            onClick={() => {
              if (userAdministrationContext.hasUnsavedChanges) {
                modal.openModal({
                  modalType: 'ConfirmationModal',
                  modalProps: {
                    type: 'error',
                    title: intl.formatMessage({
                      id: 'unsavedChanges',
                    }),
                    content: intl.formatMessage({
                      id: 'unsavedChangesDescription',
                    }),
                    confirmLabel: intl.formatMessage({
                      id: 'discard',
                    }),
                    cancelLabel: intl.formatMessage({
                      id: 'stay',
                    }),
                    onConfirm: () => {
                      modal.closeModal();
                      userAdministrationContext.setHasUnsavedChanges(false);
                      userAdministrationContext.setActiveTab(ACTIVE_TAB);
                      userAdministrationContext.setSelectedUser(null);
                      userAdministrationContext.setIsAddingUser(false);
                      userAdministrationContext.setService(null);
                    },
                  },
                });
              } else {
                userAdministrationContext.setActiveTab(ACTIVE_TAB);
                userAdministrationContext.setSelectedUser(null);
                userAdministrationContext.setIsAddingUser(false);
                userAdministrationContext.setService(null);
              }
            }}
          >
            <FormattedMessage id="activeUsers" />
          </Menu.Item>
          <Menu.Item
            name={PENDING_TAB}
            active={userAdministrationContext.activeTab === PENDING_TAB}
            onClick={() => {
              if (userAdministrationContext.hasUnsavedChanges) {
                modal.openModal({
                  modalType: 'ConfirmationModal',
                  modalProps: {
                    type: 'error',
                    title: intl.formatMessage({
                      id: 'unsavedChanges',
                    }),
                    content: intl.formatMessage({
                      id: 'unsavedChangesDescription',
                    }),
                    confirmLabel: intl.formatMessage({
                      id: 'discard',
                    }),
                    cancelLabel: intl.formatMessage({
                      id: 'stay',
                    }),
                    onConfirm: () => {
                      modal.closeModal();
                      userAdministrationContext.setHasUnsavedChanges(false);
                      userAdministrationContext.setActiveTab(PENDING_TAB);
                      userAdministrationContext.setSelectedUser(null);
                      userAdministrationContext.setIsAddingUser(false);
                      userAdministrationContext.setService(null);
                    },
                  },
                });
              } else {
                userAdministrationContext.setActiveTab(PENDING_TAB);
                userAdministrationContext.setSelectedUser(null);
                userAdministrationContext.setIsAddingUser(false);
                userAdministrationContext.setService(null);
              }
            }}
          >
            <FormattedMessage id="pendingUsers" />
          </Menu.Item>
        </Menu>
        )}

        <SearchInput
          placeholder={intl.formatMessage({ id: 'search' })}
          keyword={keyword}
          setKeyword={setKeyword}
          description="searchByNameOrAddress"
        />

        <div className={styles.usersList}>
          {filteredUsers && filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
              <UserItem
                key={user.id}
                user={user}
              />
            ))
          ) : (
            <Text
              secondary
              center
              intl="noUsersToDisplay"
              size={16}
            />
          )}
        </div>
      </div>

      <ManualScrollToTop elementRef={userListRef} />

      {canAddNewUser && (
      <div className={styles.actionContainer}>
        <Button
          primary
          className={styles.addNewUserButton}
          disabled={userAdministrationContext.isAddingUser}
          onClick={() => {
            if (userAdministrationContext.hasUnsavedChanges) {
              modal.openModal({
                modalType: 'ConfirmationModal',
                modalProps: {
                  type: 'error',
                  title: intl.formatMessage({
                    id: 'unsavedChanges',
                  }),
                  content: intl.formatMessage({
                    id: 'unsavedChangesDescription',
                  }),
                  confirmLabel: intl.formatMessage({
                    id: 'discard',
                  }),
                  cancelLabel: intl.formatMessage({
                    id: 'stay',
                  }),
                  onConfirm: () => {
                    modal.closeModal();
                    userAdministrationContext.setHasUnsavedChanges(false);
                    userAdministrationContext.setSelectedUser(null);
                    userAdministrationContext.setIsAddingUser(true);
                    userAdministrationContext.setService(null);
                  },
                },
              });
            } else {
              userAdministrationContext.setSelectedUser(null);
              userAdministrationContext.setIsAddingUser(true);
              userAdministrationContext.setService(null);
            }
          }}
        >
          <FormattedMessage id="addNewUser" />
        </Button>
      </div>
      )}
    </div>
  );
};
